export type TSessionsRateTarget = "project" | "client" | "merchant";

export const getSessionsRateRequest = (projectId: number, targetId: number, targetType: TSessionsRateTarget) => ({
	method: "overview/sessions/rate",
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: targetType === "client" ? { client_id: targetId } : targetType === "merchant" ? { mid: targetId } : {},
});

export interface ISessionsRateMinute {
	minute: string | null;
	payments: number | null;
	total: number | null;
	completed: number | null;
	failed: number | null;
}

interface ISessionsRateAverage {
	payments: number | null;
	total: number | null;
}

export interface ISessionsRateData {
	asOf: string | null;
	windowMinutes: number | null;
	lastMinute: ISessionsRateMinute | null;
	currentMinute: (ISessionsRateMinute & { partial: boolean | null; secondsElapsed: number | null }) | null;
	avg5m: ISessionsRateAverage | null;
	avg15m: ISessionsRateAverage | null;
	avgWindow: ISessionsRateAverage | null;
	peak: ISessionsRateMinute | null;
	series: (ISessionsRateMinute & { minute: string })[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isRate = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const isScopeId = (value: unknown) => value === null || (isCount(value) && value > 0);
const isOptional = <T>(value: unknown, validate: (value: unknown) => value is T): value is T | null | undefined => value === null || value === undefined || validate(value);
// Null/missing statistics are unavailable; malformed values still reject the response.
const parseOptional = <T>(value: unknown, parse: (value: unknown) => T | null): T | null | undefined => value === null || value === undefined ? null : parse(value) ?? undefined;

const isServerTime = (value: unknown, withSeconds = false): value is string => {
	if (typeof value !== "string" || !(withSeconds ? /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/ : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/).test(value)) return false;
	const normalized = `${value.replace(" ", "T")}${withSeconds ? "" : ":00"}Z`;
	const date = new Date(normalized);
	return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 19) === normalized.slice(0, 19);
};

const parseMinute = (value: unknown): ISessionsRateMinute | null => {
	if (!isRecord(value) || !isOptional(value.minute, isServerTime) || !isOptional(value.payments, isCount) || !isOptional(value.total, isCount) || !isOptional(value.completed, isCount) || !isOptional(value.failed, isCount)) return null;
	return { minute: value.minute ?? null, payments: value.payments ?? null, total: value.total ?? null, completed: value.completed ?? null, failed: value.failed ?? null };
};

const parseAverage = (value: unknown): ISessionsRateAverage | null => {
	if (!isRecord(value) || !isOptional(value.payments, isRate) || !isOptional(value.total, isRate)) return null;
	return { payments: value.payments ?? null, total: value.total ?? null };
};

export const parseSessionsRate = (value: unknown, targetId: number, targetType: TSessionsRateTarget): ISessionsRateData | null => {
	if (!isRecord(value) || !isOptional(value.as_of, (value): value is string => isServerTime(value, true)) || !isOptional(value.window_minutes, isCount) || value.window_minutes === 0 || !isRecord(value.scope) || !isOptional(value.now, isRecord) || !isOptional(value.series, Array.isArray)) return null;
	const scope = value.scope;
	if (!isScopeId(scope.client_id) || !isScopeId(scope.mid)) return null;
	if (targetType === "project" && (scope.client_id !== null || scope.mid !== null)) return null;
	if (targetType === "client" && (scope.client_id !== targetId || scope.mid !== null)) return null;
	if (targetType === "merchant" && scope.mid !== targetId) return null;
	const now = value.now ?? {};
	const lastMinute = parseOptional(now.last_minute, parseMinute);
	const currentMinute = parseOptional(now.current_minute, parseMinute);
	const peak = parseOptional(now.peak, parseMinute);
	const avg5m = parseOptional(now.avg_5m, parseAverage);
	const avg15m = parseOptional(now.avg_15m, parseAverage);
	const avgWindow = parseOptional(now.avg_window, parseAverage);
	if (lastMinute === undefined || currentMinute === undefined || peak === undefined || avg5m === undefined || avg15m === undefined || avgWindow === undefined) return null;
	const current = isRecord(now.current_minute) ? now.current_minute : {};
	if (!isOptional(current.partial, (value): value is boolean => typeof value === "boolean") || !isOptional(current.seconds_elapsed, isCount) || (current.seconds_elapsed !== null && current.seconds_elapsed !== undefined && current.seconds_elapsed >= 60)) return null;
	const currentTime = currentMinute?.minute ?? value.as_of?.slice(0, 16);
	if (currentMinute?.minute && value.as_of && currentMinute.minute !== value.as_of.slice(0, 16)) return null;
	if (currentTime && ((lastMinute?.minute && lastMinute.minute >= currentTime) || (peak?.minute && peak.minute >= currentTime))) return null;
	const series: ISessionsRateData["series"] = [];
	for (const entry of value.series ?? []) {
		if (entry === null || entry === undefined) continue;
		const minute = parseMinute(entry);
		if (!minute || !isRecord(entry) || entry.partial === true) return null;
		if (!minute.minute) continue;
		if ((currentTime && minute.minute >= currentTime) || (series.length > 0 && minute.minute <= series[series.length - 1].minute)) return null;
		series.push({ ...minute, minute: minute.minute });
	}
	return { asOf: value.as_of ?? null, windowMinutes: value.window_minutes ?? null, lastMinute, currentMinute: currentMinute ? { ...currentMinute, partial: current.partial ?? null, secondsElapsed: current.seconds_elapsed ?? null } : null, avg5m, avg15m, avgWindow, peak, series };
};
